import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiPhone, FiMail, FiInstagram, FiFacebook, FiTwitter } = FiIcons;

const Footer = () => {

  const quickLinks = [
    { name: 'Accommodation', href: '#accommodation' },
    { name: 'Experience', href: '#experience' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Contact', href: '#contact' }
  ];

  const experiences = [
    { name: 'Gongala Mountain Hike', href: '#experience' },
    { name: 'Patna Sliding Rock', href: '#experience' },
    { name: 'Tea Estate Tours', href: '#experience' },
    { name: 'Sinharaja Forest', href: '#experience' }
  ];

  return (
    <footer className="relative overflow-hidden bg-gradient-to-b from-transparent to-black/50">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16 grid lg:grid-cols-4 md:grid-cols-2 gap-8">
          {/* Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-1"
          >
            <h3 className="text-3xl font-bold text-white font-serif mb-4">
              Akkara Dahaya
            </h3>
            <p className="text-gray-300 leading-relaxed mb-6">
              Your private sanctuary in the clouds. Experience the perfect blend of 
              luxury, nature, and tranquility in the heart of Sri Lanka's tea country.
            </p>
            
            {/* Social Media */}
            <div className="flex gap-4">
              {[
                { icon: FiInstagram, href: 'https://instagram.com/akkaradahayaestate' },
                { icon: FiFacebook, href: 'https://facebook.com/akkaradahayaestate' },
                { icon: FiTwitter, href: 'https://twitter.com/akkaradahaya' }
              ].map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-10 h-10 glass-morphism rounded-full flex items-center justify-center text-gray-300 hover:text-emerald-300 transition-colors"
                >
                  <SafeIcon icon={social.icon} className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="text-xl font-semibold text-white mb-6 font-serif">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <motion.a
                    href={link.href}
                    whileHover={{ x: 5 }}
                    className="text-gray-300 hover:text-emerald-300 transition-colors duration-200"
                  >
                    {link.name}
                  </motion.a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Experiences */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="text-xl font-semibold text-white mb-6 font-serif">
              Experiences
            </h4>
            <ul className="space-y-3">
              {experiences.map((experience, index) => (
                <li key={index}>
                  <motion.a
                    href={experience.href}
                    whileHover={{ x: 5 }}
                    className="text-gray-300 hover:text-emerald-300 transition-colors duration-200"
                  >
                    {experience.name}
                  </motion.a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className="text-xl font-semibold text-white mb-6 font-serif">
              Contact Info
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiMapPin} className="w-5 h-5 text-emerald-300 mt-1" />
                <div>
                  <p className="text-gray-300">Ullinduwawa, Kolonna</p>
                  <p className="text-gray-400 text-sm">Ratnapura District, Sri Lanka</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiPhone} className="w-5 h-5 text-emerald-300" />
                <a 
                  href="tel:+94771234567" 
                  className="text-gray-300 hover:text-emerald-300 transition-colors"
                >
                  +94 77 123 4567
                </a>
              </div>
              
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiMail} className="w-5 h-5 text-emerald-300" />
                <a 
                  href="mailto:stay@akkaradahaya.com" 
                  className="text-gray-300 hover:text-emerald-300 transition-colors"
                >
                  stay@akkaradahaya.com
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="border-t border-white/10 py-8"
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © 2025 Akkara Dahaya Estate. All rights reserved.
            </p>
            
            <div className="flex gap-6 text-sm">
              <motion.a
                href="#"
                whileHover={{ y: -1 }}
                className="text-gray-400 hover:text-emerald-300 transition-colors"
              >
                Privacy Policy
              </motion.a>
              <motion.a
                href="#"
                whileHover={{ y: -1 }}
                className="text-gray-400 hover:text-emerald-300 transition-colors"
              >
                Terms of Service
              </motion.a>
              <motion.a
                href="#"
                whileHover={{ y: -1 }}
                className="text-gray-400 hover:text-emerald-300 transition-colors"
              >
                Cancellation Policy
              </motion.a>
            </div>
          </div>
          
          {/* Designer Credit */}
          <div className="mt-4 text-center">
            <p className="text-gray-500 text-xs">
              Designed and Created by{' '}
              <a 
                href="https://github.com/RajanthaR" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                Rajantha
              </a>
            </p>
          </div>
        </motion.div>

        {/* Heritage Credit */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="pb-8"
        >
          <div className="glass-card p-4 rounded-xl text-center">
            <p className="text-gray-400 text-xs">
              A heritage property managed in partnership with Associated Newspapers of Ceylon Limited
            </p>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;